import { cloneElement, isValidElement } from 'react';
import { getErrorCopy } from '@/shared/copy/errors';
import { Box, Surface, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import { Inline } from '../layout/Inline';
import {
  DataGridEmptyState,
  DataGridLoadingState,
  DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
} from '../primitives/DataGrid';
import type { ReactElement, ReactNode } from 'react';

type DataTableShellProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  state?: 'ready' | 'loading' | 'empty' | 'error';
  loadingRowCount?: number;
  emptyTitle?: string;
  emptyBody?: string;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactNode;
  children?: ReactNode;
};

export const DataTableShell = ({
  title,
  subtitle,
  actions,
  state = 'ready',
  loadingRowCount,
  emptyTitle = DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
  emptyBody,
  errorTitle = getErrorCopy('grid.errorTitle'),
  errorBody = getErrorCopy('grid.errorBody'),
  errorAction,
  children,
}: DataTableShellProps): ReactElement => {
  const showHeader = Boolean(title) || Boolean(subtitle) || Boolean(actions);
  const content =
    state === 'loading' && isValidElement(children)
      ? cloneElement(children as ReactElement<{ loading?: boolean }>, { loading: true })
      : children;

  return (
    <Surface
      padding="none"
      sx={{ paddingY: s('md'), paddingX: s('none'), minWidth: 0, width: '100%' }}
    >
      <Box sx={{ display: 'grid', gap: s('md'), minWidth: 0, width: '100%' }}>
        {showHeader ? (
          <Inline sx={{ justifyContent: 'space-between' }}>
            {title || subtitle ? (
              <Box sx={{ display: 'grid', gap: s('xxs'), minWidth: 0 }}>
                {title ? <Text role="sectionTitle">{title}</Text> : null}
                {subtitle ? <Text role="bodyMuted">{subtitle}</Text> : null}
              </Box>
            ) : null}
            {actions ? (
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {actions}
              </Box>
            ) : null}
          </Inline>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          {state === 'loading' && !isValidElement(children) ? (
            <DataGridLoadingState rowCount={loadingRowCount} />
          ) : null}
          {state === 'empty' ? <DataGridEmptyState title={emptyTitle} body={emptyBody} /> : null}
          {state === 'error' ? (
            <DataGridEmptyState title={errorTitle} body={errorBody} variant="default" />
          ) : null}
          {state === 'error' && errorAction ? (
            <Inline gap="sm" sx={{ marginTop: s('sm'), justifyContent: 'center' }}>
              {errorAction}
            </Inline>
          ) : null}
          {state === 'ready' || state === 'loading' ? content : null}
        </Box>
      </Box>
    </Surface>
  );
};
